import React from 'react';
import { useTranslation } from 'react-i18next';
import FormRow from '@/components/common/FormRow';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import InfoTooltip from '@/components/ui/info-tooltip';
import NotificationPhoneField from '@/components/page-components/project/agents/ui/NotificationPhoneField';
import type { BusinessInfoFormValue } from '@/utils/businessInfo';

interface BusinessInfoFieldsProps {
  value: BusinessInfoFormValue;
  onChange: (value: BusinessInfoFormValue) => void;
  disabled?: boolean;
  countryOptions?: Array<{ code: string; label: string }>;
  showBusinessName?: boolean;
}

const BusinessInfoFields: React.FC<BusinessInfoFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  countryOptions,
  showBusinessName = true,
}) => {
  const { t } = useTranslation();

  const patch = (partial: Partial<BusinessInfoFormValue>) => {
    onChange({ ...value, ...partial });
  };

  return (
    <>
      {showBusinessName && (
        <FormRow
          label={t(
            'agent.new.guided.profile.business_name.translation',
            'Business Name'
          )}
        >
          <Input
            value={value.businessName}
            onChange={event => patch({ businessName: event.target.value })}
            disabled={disabled}
            className="bg-card"
            placeholder={t(
              'agent.new.guided.profile.business_name_placeholder.translation',
              'e.g. Green Barber Riga'
            )}
          />
        </FormRow>
      )}
      <FormRow
        label={t(
          'agent.new.guided.profile.website.translation',
          'Website'
        )}
      >
        <Input
          type="text"
          inputMode="url"
          autoComplete="url"
          value={value.website}
          onChange={event => patch({ website: event.target.value })}
          disabled={disabled}
          className="bg-card"
          placeholder={t(
            'agent.new.guided.profile.website_placeholder.translation',
            'e.g. https://greenbarber.lv'
          )}
        />
      </FormRow>
      <FormRow
        label={t(
          'agent.new.guided.profile.address.translation',
          'Address'
        )}
      >
        <Input
          value={value.address}
          onChange={event => patch({ address: event.target.value })}
          disabled={disabled}
          className="bg-card"
          placeholder={t(
            'agent.new.guided.profile.address_placeholder.translation',
            'Street, city'
          )}
        />
      </FormRow>
      <FormRow
        label={t(
          'agent.new.guided.profile.service_area.translation',
          'Service Area'
        )}
      >
        <Input
          value={value.serviceArea}
          onChange={event => patch({ serviceArea: event.target.value })}
          disabled={disabled}
          className="bg-card"
          placeholder={t(
            'agent.new.guided.profile.service_area_placeholder.translation',
            'e.g. Riga and surrounding area'
          )}
        />
      </FormRow>
      <FormRow
        label={t(
          'agent.new.guided.profile.email.translation',
          'Public Email'
        )}
      >
        <Input
          type="email"
          autoComplete="email"
          value={value.publicEmail}
          onChange={event => patch({ publicEmail: event.target.value })}
          disabled={disabled}
          className="bg-card"
          placeholder={t(
            'agent.new.guided.profile.email_placeholder.translation',
            'e.g. info@greenbarber.lv'
          )}
        />
      </FormRow>
      <FormRow
        label={t(
          'agent.new.guided.profile.phone.translation',
          'Public Phone'
        )}
        tooltip={
          <InfoTooltip
            description={t(
              'agent.new.guided.profile.phone_hint.translation',
              'The number callers already know. The agent can share this during a call.'
            )}
          />
        }
      >
        <NotificationPhoneField
          countryCode={value.publicPhoneCountryCode}
          localNumber={value.publicPhoneLocalNumber}
          onCountryCodeChange={(code, adjustedLocalNumber) =>
            patch({
              publicPhoneCountryCode: code,
              publicPhoneLocalNumber: adjustedLocalNumber,
            })
          }
          onLocalNumberChange={localNumber =>
            patch({ publicPhoneLocalNumber: localNumber })
          }
          disabled={disabled}
          countryOptions={countryOptions}
          placeholder={t(
            'agent.new.guided.profile.phone_placeholder.translation',
            'Main business phone number'
          )}
        />
      </FormRow>
      <FormRow
        label={t(
          'agent.new.guided.profile.extra_notes.translation',
          'Additional Notes'
        )}
      >
        <Textarea
          value={value.extraNotes}
          onChange={event => patch({ extraNotes: event.target.value })}
          disabled={disabled}
          className="bg-card"
          autogrow
          placeholder={t(
            'agent.new.guided.profile.extra_notes_placeholder.translation',
            'Parking, payments, how to find you, or anything callers often ask'
          )}
        />
      </FormRow>
    </>
  );
};

export default BusinessInfoFields;
